// File Upload utility types and enums
import { IQueryParamKeys } from '../types';
import { getQueryParam } from './utils';
import { FileUploadType, ReimbursementType } from './upload-types';

export interface ISignedURLResponse {
    bucket: string;
    path: string;
    fileID: string;
    url: string;
    expires: Date;
    maxSize: number;
}

export interface IUploadInfo {
    reimbursementType: ReimbursementType;
    uploadType: FileUploadType;
}

// File upload utility class
export class FileUploadUtility {
    private readonly queryParamKeys!: IQueryParamKeys;
    private readonly csrfToken: string;
    private readonly reimbursementType: ReimbursementType | null;
    private readonly uploadType: FileUploadType | null;
    private readonly leaseId!: string | null;
    private readonly paymentToken: string | null;
    // Dynamic lease ID passed from DocumentsUpload for sequential uploads
    private readonly dynamicLeaseId: string | null;

    /**
     * @param dynamicLeaseId Optional lease ID for sequential uploads (lease → payment)
     *                       When provided, this overrides the leaseDocId query param
     */
    constructor(dynamicLeaseId?: string | null) {
        this.dynamicLeaseId = dynamicLeaseId ?? null;

        const queryParamsKeysElement: HTMLScriptElement | null =
            document.getElementById('queryParamKeys') as HTMLScriptElement;
        if (!queryParamsKeysElement?.textContent) {
            throw new Error('Query parameter keys configuration not found');
        }
        this.queryParamKeys = JSON.parse(
            queryParamsKeysElement.textContent
        ) as IQueryParamKeys;

        // Get CSRF token from the hidden element added by csrfTemplate.CSRF()
        this.csrfToken =
            document.querySelector('#token')?.getAttribute('data-token') || '';

        // Get upload info from URL query parameters
        this.reimbursementType = (getQueryParam(
            this.queryParamKeys.reimbursementType
        ) ?? '') as ReimbursementType;
        this.uploadType = (getQueryParam(this.queryParamKeys.uploadFileType) ??
            '') as FileUploadType;

        // For legacy single-file upload flows, validate params
        // For new unified flow, these may not be set initially
        if (!this.reimbursementType) {
            throw Error(
                'Invalid File Upload Params: reimbursementType required'
            );
        }

        if (this.uploadType === FileUploadType.ProofOfPayment) {
            this.leaseId = getQueryParam(this.queryParamKeys.leaseDocId);
        }

        this.paymentToken = getQueryParam(this.queryParamKeys.paymentToken);
    }

    async getSignedURL(file: File): Promise<ISignedURLResponse> {
        const leaseIdQueryParam: string = Boolean(this.leaseId)
            ? `&${this.queryParamKeys.leaseDocId}=${this.leaseId}`
            : '';
        const proofOfPaymentQueryParams: string = `&${this.queryParamKeys.paymentToken}=${this.paymentToken}${leaseIdQueryParam}`;

        const response: Response = await fetch(
            `/reimbursements/signed-url?${this.queryParamKeys.reimbursementType}=${this.reimbursementType}&${this.queryParamKeys.uploadFileType}=${this.uploadType}${proofOfPaymentQueryParams}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': this.csrfToken
                },
                body: JSON.stringify({
                    filename: file.name,
                    contentType: file.type,
                    fileType: this.uploadType
                })
            }
        );

        if (!response.ok) {
            throw new Error('500'); //Generic Server Error that will  trigger the standard user error
        }
        return (await response.json()) as unknown as ISignedURLResponse;
    }

    /**
     * Get signed URL for a specific upload type (used by unified upload flow)
     * @param file The file to upload
     * @param uploadType The type of upload (lease or payment)
     */
    async getSignedURLForType(
        file: File,
        uploadType: FileUploadType
    ): Promise<ISignedURLResponse> {
        // Build query params based on upload type
        let queryParams = `${this.queryParamKeys.reimbursementType}=${this.reimbursementType}`;
        queryParams += `&${this.queryParamKeys.uploadFileType}=${uploadType}`;
        queryParams += `&${this.queryParamKeys.paymentToken}=${this.paymentToken}`;

        // For proof of payment uploads, include the lease ID
        if (uploadType === FileUploadType.ProofOfPayment) {
            // Use dynamic lease ID (from sequential upload) or fall back to URL param
            const leaseIdToUse = this.dynamicLeaseId ?? this.leaseId;
            if (leaseIdToUse) {
                queryParams += `&${this.queryParamKeys.leaseDocId}=${leaseIdToUse}`;
            }
        }

        const response: Response = await fetch(
            `/reimbursements/signed-url?${queryParams}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': this.csrfToken
                },
                body: JSON.stringify({
                    filename: file.name,
                    contentType: file.type,
                    fileType: uploadType
                })
            }
        );

        if (!response.ok) {
            throw new Error('500');
        }
        return (await response.json()) as unknown as ISignedURLResponse;
    }

    async uploadFileToSignedURL(
        file: File,
        signedURL: string,
        maxSize: number,
        onProgress?: (progress: number) => void
    ): Promise<void> {
        // Upload file directly to GCS using signed URL with progress tracking
        const uploadResponse: Response = await this.uploadWithProgress(
            signedURL,
            file,
            maxSize,
            onProgress
        );

        const simulatedErrorCode: number = +(
            localStorage.getItem('uploadErrorCode') ?? 0
        );
        if (simulatedErrorCode) {
            throw new Error(`${simulatedErrorCode}`);
        } else if (!uploadResponse.ok) {
            throw new Error(`${uploadResponse.status}`);
        }
    }

    private async uploadWithProgress(
        url: string,
        file: File,
        maxSize: number,
        onProgress?: (progress: number) => void
    ): Promise<Response> {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            // Track upload progress
            xhr.upload.addEventListener('progress', event => {
                if (event.lengthComputable && onProgress) {
                    const progress = Math.round(
                        (event.loaded / event.total) * 100
                    );
                    onProgress(progress);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve(
                        new Response(xhr.response, {
                            status: xhr.status,
                            statusText: xhr.statusText
                        })
                    );
                } else {
                    reject(new Error(`${xhr.status}`));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Network error'));
            });

            xhr.open('PUT', url);
            xhr.setRequestHeader('Content-Type', file.type);
            xhr.setRequestHeader('x-goog-content-length-range', `0,${maxSize}`);
            xhr.send(file);
        });
    }
}
